import {applyMiddleware, combineReducers, compose, createStore} from 'redux';
import thunk from 'redux-thunk';
import logger from 'redux-logger';
import {connectRouter, routerMiddleware} from 'connected-react-router';
import reducers from '../reducers';
import {createApiMiddleware} from '../middleware/api';
import createBrowserHistory from 'history/createBrowserHistory';
import {createTransactionMiddleware} from '../middleware/transaction';
import {save, load} from 'redux-localstorage-simple';
import {createEventSourceMiddleware} from '../middleware/eventsource';

const history = createBrowserHistory();
const rootReducer = combineReducers({
  ...reducers,
  router: connectRouter(history),
});

export type AppState = ReturnType<typeof rootReducer>;

export default function configureStore(initialState?: AppState) {
  const transactionSaveOptions = {
    states: ['transactions'],
    namespace: '',
  };

  const authSaveOptions = {
    states: ['auth'],
    ignoreStates: ['auth.isLoading'],
    namespace: '',
  };

  const middleware = [
    thunk,
    routerMiddleware(history),
    createApiMiddleware(),
    save(transactionSaveOptions),
    save(authSaveOptions),
    createTransactionMiddleware(),
    createEventSourceMiddleware(),
  ];

  if (
    process.env.NODE_ENV !== 'production' &&
    process.env.REACT_APP_REDUX_LOGGER === 'true'
  ) {
    middleware.push(logger as any);
  }

  const enhancers = [];
  const windowIfDefined =
    typeof window === 'undefined' ? null : (window as any);
  if (windowIfDefined && windowIfDefined.__REDUX_DEVTOOLS_EXTENSION__) {
    enhancers.push(windowIfDefined.__REDUX_DEVTOOLS_EXTENSION__());
  }

  const store = createStore(
    rootReducer,
    {
      ...(initialState || {}),
      ...load(transactionSaveOptions),
      ...load(authSaveOptions),
    },
    compose(applyMiddleware(...middleware), ...enhancers),
  );

  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
      if (!event.key || event.key.includes('transactions') || event.key.includes('redux_localstorage_simple')) {
        const loadedState: any = load(transactionSaveOptions);
        if (loadedState && loadedState.transactions) {
          store.dispatch({
            type: 'SYNC_TRANSACTIONS_STATE',
            payload: loadedState.transactions,
          });
        }
      }

      if (!event.key || event.key.includes('auth') || event.key.includes('redux_localstorage_simple')) {
        const loadedAuthState: any = load(authSaveOptions);
        if (loadedAuthState && loadedAuthState.auth) {
          store.dispatch({
            type: 'SYNC_AUTH_STATE',
            payload: loadedAuthState.auth,
          });
        }
      }
    });
  }

  return store;
}

export {history};

// export type AppState = ReturnType<typeof rootReducer>;
